import React, { useState } from 'react';
import { Camera, X, Loader2 } from 'lucide-react';
import { ImageSourceButtons } from './ImageSourceButtons';
import { estimateDataUrlBytes, fileToCompressedDataUrl, formatBytesShort, MAX_PHOTO_BYTES } from '../../lib/imageResize';
import { uploadsApi, hasAuthToken } from '../../api';
import { mediaSrc } from '../../lib/mediaUrl';

interface PhotoUploadProps {
  value?: string;
  onChange: (value: string) => void;
  label?: string;
  /**
   * Facility / customer record id. Required to store as customer_<id> on Cloudinary
   * (Database-ID-as-filename). When set and the user is signed in, photos upload
   * to the server; the form stores the resolved URL (API persists the short filename).
   */
  customerId?: string | null;
}

/**
 * Applicant photo picker. With customerId + auth, uploads as customer_<id>
 * (overwrite). Without auth, falls back to a local compressed data URL.
 */
export default function PhotoUpload({
  value,
  onChange,
  label = 'Tap to add applicant photo',
  customerId,
}: PhotoUploadProps) {
  const [busy, setBusy] = useState(false);
  const [sizeNote, setSizeNote] = useState<string | null>(null);

  const handleFiles = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setBusy(true);
    setSizeNote(null);
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      const kb = formatBytesShort(estimateDataUrlBytes(dataUrl));

      // Always keep the compressed photo itself. Swapping in customer_<id>.jpg
      // replaces a visible image with a link that is not on Cloudinary.
      onChange(dataUrl);
      if (hasAuthToken() && customerId) {
        try {
          await uploadsApi.image(dataUrl, { kind: 'customer', customerId });
          setSizeNote(`Photo saved (${kb}).`);
        } catch {
          setSizeNote(`Photo kept on this record (${kb}).`);
        }
      } else {
        setSizeNote(`Compressed to ${kb} (max ${formatBytesShort(MAX_PHOTO_BYTES)})`);
      }
    } catch {
      setSizeNote('Could not process this image. Try another photo.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-start gap-4">
      <div className="relative h-28 w-28 shrink-0 overflow-hidden rounded-xl border-2 border-dashed border-slate-300 bg-slate-50">
        {value ? (
          <>
            <img src={mediaSrc(value)} alt="Applicant" className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => {
                onChange('');
                setSizeNote(null);
              }}
              className="absolute right-1 top-1 rounded-full bg-black/55 p-1 text-white hover:bg-black/75"
              aria-label="Remove photo"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-400">
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Camera className="h-6 w-6" />}
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <ImageSourceButtons onFiles={(f) => void handleFiles(f)} disabled={busy} cameraFacing="user" />
        <p className="mt-1.5 text-xs text-slate-500">{label}</p>
        <p className="mt-0.5 text-[11px] text-slate-400">
          Images over {formatBytesShort(MAX_PHOTO_BYTES)} are auto-compressed while keeping quality.
        </p>
        {sizeNote ? <p className="mt-1 text-[11px] font-medium text-emerald-700">{sizeNote}</p> : null}
      </div>
    </div>
  );
}
